import { Button, Group, Radio, TextInput } from '@any-ui-react/core';
import { vestResolver } from '@hookform/resolvers/vest';
import classNames from 'classnames';
import { useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { toast } from '@/common/components/shared/toaster/toast';
import { EmployeeExportFormat, EmployeeImportFormat } from '@/common/graphql';

import { importTypeLabel, importTypeOptions } from './const';
import { ImportTemplateAction } from './ImportTemplateAction';
import { importValidationSuite } from './import.validator';
import { type ImportModalFormData, MANAGERIAL_ACCOUNTING_IMPORT_TYPE } from './types';
import { useImportModalSubmit } from './useImportModalSubmit';

interface Props {
  onClose: () => void;
  /**
   * Called on a successful import submission, before the modal would otherwise close. When provided
   * (by the Tabs host), the host switches to its History tab instead of closing — mirroring
   * `EmployeeListExportModal`'s post-submit `setActiveTab('history')`. When omitted, the form keeps
   * its original behavior of closing the modal via `onClose`.
   */
  onImported?: () => void;
}

export const ImportModalForm = ({ onClose, onImported }: Props) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ImportModalFormData>({
    resolver: vestResolver(importValidationSuite),
    defaultValues: {
      action: null,
      link: '',
    },
  });

  const action = useWatch({ control, name: 'action' });
  const link = useWatch({ control, name: 'link' });

  const { loading, setTemplateExportInProgress, handleUpload } = useImportModalSubmit({
    onSubmitSuccess: () => {
      toast.success('Request has been sent. Result will be notified to your email.');
      if (onImported) {
        onImported();
      } else {
        onClose();
      }
    },
  });

  const isValidLink = useMemo(() => link.startsWith('https://docs.google.com/spreadsheets/d/'), [link]);

  const importTemplateAction = useMemo(() => {
    let format: EmployeeExportFormat | undefined;

    switch (action) {
      case EmployeeImportFormat.Create:
        format = EmployeeExportFormat.CreateTemplate;
        break;
      case MANAGERIAL_ACCOUNTING_IMPORT_TYPE:
        format = EmployeeExportFormat.ManagerialAccountingImportTemplate;
        break;
    }

    if (!format) {
      return null;
    }

    return (
      <ImportTemplateAction
        format={format}
        onCompleted={() => setTemplateExportInProgress(false)}
        onStarted={() => setTemplateExportInProgress(true)}
      />
    );
  }, [action, setTemplateExportInProgress]);

  return (
    <>
      <div className="flex flex-col space-y-4">
        <span>Import data in bulk via Google spreadsheet.</span>

        <Controller
          control={control}
          name="action"
          render={({ field: { onChange, ...fields } }) => (
            <Radio.Group {...fields} classNames={{ error: 'mt-2' }} error={errors.action?.message} onChange={onChange}>
              <Group className="flex flex-col items-start justify-start gap-y-2">
                {importTypeOptions.map(it => (
                  <Radio key={it} disabled={loading} label={importTypeLabel[it]} value={it} />
                ))}
              </Group>
            </Radio.Group>
          )}
        />

        <div className="flex flex-col">
          <span>Please paste the link to your spreadsheet below.</span>
          {/* The help slide deck covers the Employee import flow only; Managerial Accounting has no matching guide yet. */}
          {action !== MANAGERIAL_ACCOUNTING_IMPORT_TYPE && (
            <a
              className="text-link"
              href="https://docs.google.com/presentation/d/1cAmf9kUWY2wJbTmmm5uw10KVjqIPuW6Nmf2DVV9V99I/edit?slide=id.g323c25c4f53_1_15#slide=id.g323c25c4f53_1_15"
              rel="noreferrer"
              target="_blank"
            >
              Need help importing?
            </a>
          )}
        </div>

        <div className="relative">
          <TextInput
            {...register('link')}
            disabled={loading || !action}
            error={errors.link?.message}
            label="Google Spreadsheet Link"
            name="link"
            placeholder="https://docs.google.com/spreadsheets/d/..."
            required={true}
            type="text"
          />

          {importTemplateAction}
        </div>
      </div>

      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className={classNames({ 'bg-blue-6': action && isValidLink })}
          disabled={!action || !isValidLink}
          loading={loading}
          onClick={handleSubmit(handleUpload)}
        >
          Upload
        </Button>
      </ModalFooter>
    </>
  );
};
